import { animate, stagger } from "vendor";

// Automatically appends the next page of products to the collection product list when the user nears the bottom of
// the grid. The regular pagination is kept in the markup as a no-JS / crawler fallback and is shown again if a fetch fails.
// When facets or sorting change, the whole section is re-rendered, so a fresh <infinite-scroll> element takes over.
class InfiniteScroll extends HTMLElement {
  #observer;
  #abortController;
  #isLoading = false;

  connectedCallback() {
    this.productList = document.getElementById(this.getAttribute("target"));

    if (!this.productList || !this.nextUrl || !("IntersectionObserver" in window)) {
      return;
    }

    this.classList.add("is-enhanced");

    this.#observer = new IntersectionObserver(
      (entries) => entries.some((entry) => entry.isIntersecting) && this.#loadNextPage(),
      { rootMargin: "0px 0px 800px 0px" },
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  get nextUrl() {
    return this.getAttribute("next-url");
  }

  async #loadNextPage() {
    if (this.#isLoading || !this.nextUrl) {
      return;
    }

    this.#isLoading = true;
    this.#abortController = new AbortController();
    this.setAttribute("aria-busy", "true");

    try {
      const url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set("section_id", this.getAttribute("section-id"));

      const response = await fetch(url.toString(), { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Infinite scroll request failed with status ${response.status}`);
      }

      const html = new DOMParser().parseFromString(await response.text(), "text/html"),
        newProductList = html.querySelector(`#${CSS.escape(this.productList.id)}`),
        newInfiniteScroll = html.querySelector("infinite-scroll"),
        newCards = newProductList ? Array.from(newProductList.children, (card) => document.importNode(card, true)) : [];

      this.productList.append(...newCards);
      this.#revealCards(newCards);

      const nextUrl = newInfiniteScroll?.getAttribute("next-url");

      if (nextUrl && newCards.length > 0) {
        this.setAttribute("next-url", nextUrl);

        // Keep the fallback pagination in sync with the last loaded page.
        const currentPagination = this.querySelector(".pagination"),
          newPagination = newInfiniteScroll.querySelector(".pagination");

        if (currentPagination && newPagination) {
          currentPagination.replaceWith(document.importNode(newPagination, true));
        }


        this.#isLoading = false;
        this.removeAttribute("aria-busy");

        // If the sentinel is still in view (e.g. on very tall screens), keep loading.
        this.#observer.unobserve(this);
        this.#observer.observe(this);
      } else {
        this.#observer.disconnect();
        this.remove();
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      // Fall back to the regular pagination so the user can still browse.
      this.#observer.disconnect();
      this.classList.remove("is-enhanced");
      this.removeAttribute("aria-busy");
      this.#isLoading = false;
    }
  }

  #revealCards(cards) {
    const revealCards = cards.filter((card) => card.getAttribute("reveal-on-scroll") === "true");

    if (revealCards.length === 0 || !window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      return;
    }

    animate(
      revealCards,
      { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] },
      { duration: 0.2, easing: "ease-in-out", delay: stagger(0.05, { easing: "ease-out" }) },
    );
  }
}

if (!window.customElements.get("infinite-scroll")) {
  window.customElements.define("infinite-scroll", InfiniteScroll);
}
